import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../store';
import { updateProfile } from '../../store/slices/authSlice';
import { CheckCircleIcon, ExclamationCircleIcon, UserCircleIcon } from '@heroicons/react/24/solid';

interface ProfileFormData {
  firstName: string;
  lastName: string;
  company: string;
  email: string;
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

interface FormErrors {
  firstName?: string;
  lastName?: string;
  company?: string;
  currentPassword?: string;
  newPassword?: string;
  confirmPassword?: string;
}

export default function ProfilePage() {
  const dispatch = useDispatch<AppDispatch>();
  const { user, isLoading, error } = useSelector((state: RootState) => state.auth);
  const [isEditing, setIsEditing] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [activeTab, setActiveTab] = useState<'info' | 'password'>('info');
  const [formData, setFormData] = useState<ProfileFormData>({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    company: user?.company?.name || '',
    email: user?.email || '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  // Reset form data when user data changes
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        company: user.company?.name || '',
        email: user.email || '',
      }));
    }
  }, [user]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    setFormData({
      ...formData,
      [name]: value,
    });

    // Clear validation errors when field is edited
    if (formErrors[name as keyof FormErrors]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const validateForm = (): boolean => {
    const errors: FormErrors = {};
    let isValid = true;

    if (!formData.firstName.trim()) {
      errors.firstName = 'Le prénom est requis';
      isValid = false;
    }

    if (!formData.lastName.trim()) {
      errors.lastName = 'Le nom est requis';
      isValid = false;
    }

    if (!formData.company.trim()) {
      errors.company = 'L\'entreprise est requise';
      isValid = false;
    }

    // Only validate password fields if user is trying to change password
    if (formData.newPassword || formData.currentPassword) {
      if (!formData.currentPassword) {
        errors.currentPassword = 'Le mot de passe actuel est requis';
        isValid = false;
      }

      if (formData.newPassword && formData.newPassword.length < 8) {
        errors.newPassword = 'Le mot de passe doit contenir au moins 8 caractères';
        isValid = false;
      }

      if (formData.newPassword !== formData.confirmPassword) {
        errors.confirmPassword = 'Les mots de passe ne correspondent pas';
        isValid = false;
      }
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      // Only include password fields if a new password is being set
      const updateData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        company: formData.company,
        ...(formData.newPassword && {
          currentPassword: formData.currentPassword,
          newPassword: formData.newPassword,
        }),
      };

      await dispatch(updateProfile(updateData));
      setSuccessMessage('Profil mis à jour avec succès');
      setIsEditing(false);
      
      // Reset password fields
      setFormData(prev => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      }));

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (err) {
      console.error(err);
      // Error handling is managed by Redux
    }
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setFormErrors({});
    setActiveTab('info');
    
    // Reset form data to original user data
    setFormData({
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      company: user?.company?.name || '',
      email: user?.email || '',
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8 flex items-center">
        <div className="flex-shrink-0 mr-3">
          <UserCircleIcon className="h-16 w-16 text-primary-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Mon profil</h1>
          <p className="text-sm text-gray-500">
            Gérez vos informations personnelles et vos préférences de compte
          </p>
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="mb-6 rounded-md bg-green-50 p-4 flex items-start">
          <CheckCircleIcon className="h-5 w-5 text-green-400 mr-2 mt-0.5" />
          <div className="text-sm text-green-700">{successMessage}</div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-6 rounded-md bg-red-50 p-4 flex items-start">
          <ExclamationCircleIcon className="h-5 w-5 text-red-400 mr-2 mt-0.5" />
          <div className="text-sm text-red-700">{error}</div>
        </div>
      )}

      {/* Profile Form */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {/* Edit/View Mode Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-lg font-medium text-gray-900">
            {isEditing ? 'Modifier mon profil' : 'Informations personnelles'}
          </h2>
          
          {!isEditing && (
            <button
              type="button"
              onClick={() => setIsEditing(true)}
              className="btn-primary text-sm"
            >
              Modifier
            </button>
          )}
        </div>

        {/* Form Content */}
        <form onSubmit={handleSubmit}>
          {isEditing ? (
            <div className="p-6">
              {/* Tabs for Personal Info vs Password */}
              <div className="border-b border-gray-200 mb-6">
                <nav className="-mb-px flex space-x-8">
                  <button
                    type="button"
                    onClick={() => setActiveTab('info')}
                    className={`pb-3 px-1 border-b-2 font-medium text-sm ${
                      activeTab === 'info'
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Informations personnelles
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab('password')}
                    className={`pb-3 px-1 border-b-2 font-medium text-sm ${
                      activeTab === 'password'
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Mot de passe
                  </button>
                </nav>
              </div>

              {activeTab === 'info' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <div>
                      <label htmlFor="firstName" className="form-label">
                        Prénom <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="firstName"
                        id="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        className={`input-field ${formErrors.firstName ? 'ring-red-500 focus:ring-red-500' : ''}`}
                        required
                      />
                      {formErrors.firstName && (
                        <p className="mt-1 text-sm text-red-600">{formErrors.firstName}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="lastName" className="form-label">
                        Nom <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="lastName"
                        id="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        className={`input-field ${formErrors.lastName ? 'ring-red-500 focus:ring-red-500' : ''}`}
                        required
                      />
                      {formErrors.lastName && (
                        <p className="mt-1 text-sm text-red-600">{formErrors.lastName}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="email" className="form-label">
                        Email
                      </label>
                      <input
                        type="email"
                        name="email"
                        id="email"
                        value={formData.email}
                        disabled
                        className="input-field bg-gray-50 text-gray-500"
                        aria-description="L'email ne peut pas être modifié"
                      />
                      <p className="mt-1 text-xs text-gray-500">L'email ne peut pas être modifié</p>
                    </div>

                    <div>
                      <label htmlFor="company" className="form-label">
                        Entreprise <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="company"
                        id="company"
                        value={formData.company}
                        onChange={handleChange}
                        className={`input-field ${formErrors.company ? 'ring-red-500 focus:ring-red-500' : ''}`}
                        required
                      />
                      {formErrors.company && (
                        <p className="mt-1 text-sm text-red-600">{formErrors.company}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'password' && (
                <div className="space-y-4">
                  <div>
                    <label htmlFor="currentPassword" className="form-label">
                      Mot de passe actuel {formData.newPassword && <span className="text-red-500">*</span>}
                    </label>
                    <input
                      type="password"
                      name="currentPassword"
                      id="currentPassword"
                      value={formData.currentPassword}
                      onChange={handleChange}
                      className={`input-field ${formErrors.currentPassword ? 'ring-red-500 focus:ring-red-500' : ''}`}
                    />
                    {formErrors.currentPassword && (
                      <p className="mt-1 text-sm text-red-600">{formErrors.currentPassword}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="newPassword" className="form-label">
                      Nouveau mot de passe
                    </label>
                    <input
                      type="password"
                      name="newPassword"
                      id="newPassword"
                      value={formData.newPassword}
                      onChange={handleChange}
                      className={`input-field ${formErrors.newPassword ? 'ring-red-500 focus:ring-red-500' : ''}`}
                    />
                    {formErrors.newPassword ? (
                      <p className="mt-1 text-sm text-red-600">{formErrors.newPassword}</p>
                    ) : (
                      <p className="mt-1 text-xs text-gray-500">Minimum 8 caractères</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="confirmPassword" className="form-label">
                      Confirmer le nouveau mot de passe
                    </label>
                    <input
                      type="password"
                      name="confirmPassword"
                      id="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      className={`input-field ${formErrors.confirmPassword ? 'ring-red-500 focus:ring-red-500' : ''}`}
                    />
                    {formErrors.confirmPassword && (
                      <p className="mt-1 text-sm text-red-600">{formErrors.confirmPassword}</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-6">
                <div>
                  <h3 className="text-xs uppercase tracking-wider text-gray-500 font-medium">Prénom</h3>
                  <p className="mt-1 text-gray-900">{user?.firstName || '-'}</p>
                </div>
                <div>
                  <h3 className="text-xs uppercase tracking-wider text-gray-500 font-medium">Nom</h3>
                  <p className="mt-1 text-gray-900">{user?.lastName || '-'}</p>
                </div>
                <div>
                  <h3 className="text-xs uppercase tracking-wider text-gray-500 font-medium">Email</h3>
                  <p className="mt-1 text-gray-900">{user?.email || '-'}</p>
                </div>
                <div>
                  <h3 className="text-xs uppercase tracking-wider text-gray-500 font-medium">Entreprise</h3>
                  <p className="mt-1 text-gray-900">{user?.company?.name || '-'}</p>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          {isEditing && (
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3">
              <button
                type="button"
                onClick={cancelEdit}
                className="btn-secondary text-sm"
              >
                Annuler
              </button>
              <button
                type="submit"
                className="btn-primary text-sm flex items-center"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Enregistrement...
                  </>
                ) : 'Enregistrer'}
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
} 